import { US_STATES, US_CITIES_BY_STATE } from '../data/usLocations';

export interface LocationDetectionResult {
  stateCode: string;
  stateName: string;
  cityName: string;
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  source: 'gps' | 'ip' | 'fallback';
}

export async function detectUserLocation(): Promise<LocationDetectionResult> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Browser geolocation is not supported on this device.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        try {
          // Reverse geocoding via BigDataCloud client API (free & client-side)
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          if (response.ok) {
            const data = await response.json();
            
            // Extract state code (e.g. "US-NY" -> "NY", or "California")
            let rawState = data.principalSubdivisionCode || data.principalSubdivision || '';
            if (rawState.startsWith('US-')) {
              rawState = rawState.replace('US-', '');
            } else if (rawState.length > 2) {
              const foundState = US_STATES.find(
                (s) => s.name.toLowerCase() === rawState.toLowerCase() || s.code.toLowerCase() === rawState.toLowerCase()
              );
              if (foundState) rawState = foundState.code;
            }

            // Verify matched state
            const stateObj = US_STATES.find((s) => s.code.toUpperCase() === rawState.toUpperCase());
            const matchedStateCode = stateObj ? stateObj.code : 'CA';
            const matchedStateName = stateObj ? stateObj.name : 'California';

            // Extract city / town
            let detectedCity = data.city || data.locality || data.localityInfo?.administrative?.[2]?.name || 'Los Angeles';
            if (typeof detectedCity === 'string' && detectedCity.startsWith('City of ')) {
              detectedCity = detectedCity.replace('City of ', '');
            }

            // Find matching city in dataset if possible
            const stateCities = US_CITIES_BY_STATE[matchedStateCode] || [];
            const exactOrPartialMatch = stateCities.find(
              (c) => c.toLowerCase() === detectedCity.toLowerCase()
            );

            const finalCity = exactOrPartialMatch || detectedCity;

            resolve({
              stateCode: matchedStateCode,
              stateName: matchedStateName,
              cityName: finalCity,
              latitude,
              longitude,
              accuracyMeters: accuracy,
              source: 'gps',
            });
            return;
          }
        } catch (err) {
          console.warn('Reverse geocode API call failed, falling back to position coordinates:', err);
        }

        // Fallback coordinate approximation
        const estimatedStateCode = estimateUSStateByCoords(latitude, longitude);
        const stateObj = US_STATES.find((s) => s.code === estimatedStateCode) || US_STATES[4];
        const defaultCity = US_CITIES_BY_STATE[stateObj.code]?.[0] || 'Main City';

        resolve({
          stateCode: stateObj.code,
          stateName: stateObj.name,
          cityName: defaultCity,
          latitude,
          longitude,
          accuracyMeters: accuracy,
          source: 'gps',
        });
      },
      (error) => {
        let msg = 'Geolocation permission denied or timed out.';
        if (error.code === error.PERMISSION_DENIED) {
          msg = 'Location permission was denied. Please select your region manually.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          msg = 'Location position is currently unavailable.';
        } else if (error.code === error.TIMEOUT) {
          msg = 'Location request timed out.';
        }
        reject(new Error(msg));
      },
      {
        enableHighAccuracy: true,
        timeout: 9000,
        maximumAge: 60000,
      }
    );
  });
}

function estimateUSStateByCoords(lat: number, lng: number): string {
  if (lat >= 40.0 && lat <= 45.0 && lng >= -79.8 && lng <= -71.8) return 'NY';
  if (lat >= 32.5 && lat <= 42.0 && lng >= -124.4 && lng <= -114.1) return 'CA';
  if (lat >= 24.5 && lat <= 31.0 && lng >= -87.6 && lng <= -80.0) return 'FL';
  if (lat >= 25.8 && lat <= 36.5 && lng >= -106.6 && lng <= -93.5) return 'TX';
  if (lat >= 36.9 && lat <= 42.5 && lng >= -91.5 && lng <= -87.5) return 'IL';
  if (lat >= 38.4 && lat <= 42.0 && lng >= -84.8 && lng <= -80.5) return 'OH';
  if (lat >= 30.3 && lat <= 35.0 && lng >= -85.6 && lng <= -80.8) return 'GA';
  if (lat >= 45.5 && lat <= 49.0 && lng >= -124.8 && lng <= -116.9) return 'WA';
  return 'CA';
}
